import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn.ts";
import { Loader2 } from "lucide-react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:   "bg-brand text-white hover:bg-brand-600 active:bg-brand-700 disabled:bg-brand-300",
  secondary: "bg-surface-100 text-surface-700 hover:bg-surface-200 active:bg-surface-300 disabled:opacity-50",
  ghost:     "bg-transparent text-surface-600 hover:bg-surface-100 active:bg-surface-200 disabled:opacity-40",
  danger:    "bg-danger text-white hover:bg-red-600 active:bg-red-700 disabled:bg-red-300",
  outline:   "bg-white border border-surface-200 text-surface-700 hover:bg-surface-50 disabled:opacity-50",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-[12px] gap-1.5",
  md: "h-9 px-4 text-[13px] gap-2",
  lg: "h-10 px-5 text-[13px] gap-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, fullWidth = false, disabled, className, children, ...props }, ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-lg font-medium transition-colors duration-150 disabled:cursor-not-allowed",
        VARIANTS[variant], SIZES[size], fullWidth && "w-full", className,
      )}
      {...props}
    >
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
});
